import {
  Box,
  Flex,
  FocusLock,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Text,
  TextProps,
  VStack,
} from '@chakra-ui/react';
import {
  CompetitivenessLevel,
  UserObject,
} from '@client/graphql/__generated__/types';
import { ArrowRightIcon } from 'lucide-react';
import { useRef } from 'react';
import { AgentChatBubble } from '../../Agent/AgentChatBubble';
import { CompetitivenessLabel } from './CompetitivenessLabel';

const LABEL_STYLE: TextProps = {
  color: 'whiteAlpha.600',
  fontSize: 'sm',
  fontWeight: 'medium',
  textTransform: 'uppercase',
};

type Props = {
  user: Partial<Pick<UserObject, 'fullName' | 'profilePicture'>>;
  prevCompetitiveness: CompetitivenessLevel;
  newCompetitiveness: CompetitivenessLevel;
  reason?: string;
};

export const CompetitivenessAdjustmentPopover = ({
  user,
  prevCompetitiveness,
  newCompetitiveness,
  reason,
}: Props) => {
  const focusRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Popover initialFocusRef={focusRef} placement="bottom-end">
      <PopoverTrigger>
        <Box
          bottom={3}
          data-testid="competitiveness-adjustment-popover-trigger"
          position="relative"
        >
          <AgentChatBubble user={user} />
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          backgroundColor="bg.mainDark"
          maxWidth={{ base: '300px', md: 'full' }}
          px={4}
          w="full"
        >
          <FocusLock>
            <PopoverArrow backgroundColor="bg.mainDark" />
            <PopoverCloseButton color="whiteAlpha.600" />
            <PopoverHeader>
              <Text {...LABEL_STYLE}>Competitiveness Adjustment</Text>
            </PopoverHeader>
            <PopoverBody>
              <VStack alignItems="flex-start" gap={2} py={2} w="full">
                <Flex
                  alignItems="center"
                  flexShrink={0}
                  flexWrap={{ base: 'wrap', md: 'nowrap' }}
                  gap={2}
                >
                  <CompetitivenessLabel level={prevCompetitiveness} />
                  <Icon as={ArrowRightIcon} />
                  <CompetitivenessLabel level={newCompetitiveness} />
                </Flex>
                {reason && (
                  <>
                    <Text
                      color="whiteAlpha.600"
                      fontSize="sm"
                      fontWeight="medium"
                      mt={4}
                      textTransform="uppercase"
                    >
                      Agent Note
                    </Text>
                    <Text>{reason}</Text>
                  </>
                )}
              </VStack>
            </PopoverBody>
          </FocusLock>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
